import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";

type TDataGridPaginationProps = {
  page: number;
  pageCount: number;
  pageSize: number;
  total: number;
  offset: number;
  rowCount: number;
  pageSizeOptions: number[];
  onPrev: () => void;
  onNext: () => void;
  onPageSizeChange: (size: number) => void;
};

/**
 * Pagination footer: range summary, rows-per-page selector, prev/next.
 * Works for both offset and page modes (they share the same page-index math).
 */
export function DataGridPagination({
  page,
  pageCount,
  pageSize,
  total,
  offset,
  rowCount,
  pageSizeOptions,
  onPrev,
  onNext,
  onPageSizeChange,
}: TDataGridPaginationProps) {
  const start = total === 0 ? 0 : offset + 1;
  const end = Math.min(offset + rowCount, total);

  return (
    <div className="flex flex-col gap-3 border-t border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
      <div className="flex items-center gap-4">
        <p className="text-sm text-muted-foreground">
          {total > 0 ? `Showing ${start}–${end} of ${total}` : "No results"}
        </p>
        <div className="flex items-center gap-2">
          <label
            htmlFor="grid-page-size"
            className="text-sm text-muted-foreground"
          >
            Rows
          </label>
          <Select
            id="grid-page-size"
            className="h-8 w-[4.5rem]"
            value={String(pageSize)}
            options={pageSizeOptions.map((size) => ({ label: String(size), value: String(size) }))}
            onValueChange={(value) => onPageSizeChange(Number(value))}
            aria-label="Rows per page"
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onPrev}
          disabled={page <= 1}
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>
        <span className="px-1 text-sm text-muted-foreground">
          {page} / {pageCount}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={onNext}
          disabled={page >= pageCount}
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
